// Cue stroke model: hold Space, draw the mouse back, push it forward through the ball.
// The controls are the original ones; this module only decides WHEN a stroke fires and HOW HARD,
// from the real speed of the hand (distance over time), so it no longer depends on how many
// mousemove events the browser happens to deliver (mouse polling rate, display refresh, coalescing).
//
// Units: "pull" is the cue's draw-back in cue pixels (positive = back, towards the player):
// mouse pixels × the stroke sensitivity setting (1 by default, so by default cue px = mouse px).
// The cue is drawn at Distance = REST_DISTANCE + pull / PX_PER_UNIT, same mapping as before.
// Because sensitivity scales cue travel, it also scales power for a given hand speed.

export const STROKE = {
    REST_DISTANCE: 1.3,   // cue tip distance at rest (unchanged)
    PX_PER_UNIT: 200,     // mouse px per scene unit of cue travel (unchanged: movementY / 200)
    FIRE_PX: 38,          // the cue reaches the ball 38 px forward of rest (old threshold 1.11)
    // An unarmed push stops the cue this far from the ball so it doesn't look like a hit that didn't fire:
    // 14 px = 0.07 scene units, about 6 mm at this table's scale (ball = 0.6265 units = 52.5 mm).
    UNARMED_GAP_PX: 14,
    MIN_BACK_PX: 30,      // a back-stroke of at least this much arms the shot (no nudge shots)
    // The original game had no limit on the draw-back. This bound is only a safety net: 3400 cue px
    // = 17 table units, about the length of a real cue (1.45 m at this table's scale).
    MAX_BACK_PX: 3400,
    REVERSAL_PX: 30,      // backward wobble smaller than a real back-stroke (MIN_BACK_PX) is jitter, not a new stroke
    MAX_EVENT_SPAN_MS: 50,// after a pause (page rendering normally), an event is assumed to cover at most 50 ms
    STALL_FRAMES: 2.5,    // no frame for this many usual frame intervals (and > 50 ms) = the browser stalled
    WINDOW_MS: 80,        // stroke speed = forward distance over the last 80 ms
    SMOOTH_MS: 4,         // wobble filter used only to find the top of the back-stroke inside the window
    GAIN: 8.33,           // ball speed per (px/ms); = the old feel with 60 Hz-coalesced events
    MIN_SPEED: 0.3,
    MAX_SPEED: 60,
};

interface Sample { t: number; dy: number; span: number }

function median(xs: number[]) {
    const a = xs.slice().sort((p, q) => p - q);
    return a.length ? a[a.length >> 1] : 0;
}

export interface StrokeResult {
    fire: boolean;
    speed: number;        // ball speed if fired (0 otherwise)
}

export class StrokeTracker {
    pull = 0;                 // cue px, clamped to [-FIRE_PX, MAX_BACK_PX]
    sensitivity = 1;          // cue px per mouse px (stroke sensitivity setting)
    armed = false;            // a real back-stroke happened since the last reset
    blockedAt = -Infinity;    // last time an unarmed push was stopped short of the ball (for the "pull back first" hint)
    private minPull = 0;
    private samples: Sample[] = [];
    private lastT = -Infinity;
    private frames: number[] = [];   // recent render-loop times (see frame())

    /**
     * Called once per rendered frame (performance.now()). Lets the tracker tell a browser stall (no frame
     * rendered during the gap before an event) from a hand that simply paused (frames kept coming).
     */
    frame(t: number) {
        this.frames.push(t);
        if (this.frames.length > 32) this.frames.shift();
    }

    private lastFrameBefore(t: number): number {
        for (let i = this.frames.length - 1; i >= 0; i--) if (this.frames[i] < t) return this.frames[i];
        return -Infinity;
    }

    /** Median interval between the recent frames rendered before `t` (0 when fewer than 3 intervals are known). */
    private frameInterval(t: number): number {
        const before = this.frames.filter(f => f < t).slice(-17);
        if (before.length < 4) return 0;
        const d: number[] = [];
        for (let i = 1; i < before.length; i++) d.push(before[i] - before[i - 1]);
        return median(d);
    }

    /**
     * How long the movement of the event at `t` may have taken. It happened between the previous event and
     * this one (the gap). After a pause with the page rendering normally, the movement started at the end of
     * the pause, so it is assumed to take at most MAX_EVENT_SPAN_MS (unchanged feel). But when the browser
     * stalled (it rendered no frame for much longer than usual, then delivered the movement it held back as
     * one event), the movement may really have taken the whole gap, so it's spread over the whole gap: a
     * stall can make a shot weaker than the hand, never stronger. Up to round 5 the cap applied here too,
     * and a 100-500 ms stall read 2-3.6x too hard.
     * "Much longer than usual" = more than STALL_FRAMES x the median recent frame interval, and more than
     * MAX_EVENT_SPAN_MS. So on a slow machine (say 10 FPS: a frame every 100 ms) the normal wait for the
     * next frame isn't mistaken for a stall, and the event keeps the old spread.
     */
    private eventSpan(t: number): number {
        const gap = Math.max(0.5, t - this.lastT);
        if (gap <= STROKE.MAX_EVENT_SPAN_MS) return gap;
        const sinceFrame = t - this.lastFrameBefore(t);    // Infinity when no frame is known: treat as a stall
        const stall = sinceFrame > Math.max(STROKE.MAX_EVENT_SPAN_MS, STROKE.STALL_FRAMES * this.frameInterval(t));
        return stall ? Math.max(STROKE.MAX_EVENT_SPAN_MS, Math.min(gap, sinceFrame)) : STROKE.MAX_EVENT_SPAN_MS;
    }

    reset(t: number) {
        this.pull = 0;
        this.armed = false;
        this.blockedAt = -Infinity;
        this.minPull = 0;
        this.samples = [];
        this.lastT = t;
    }

    /**
     * Space pressed again: carry on from where the cue is now. Like the original game, the cue keeps its
     * draw-back (nothing moves on the key press). Only the arming and the speed history start over, so the
     * 30 px minimum back-stroke is measured from here and a re-press can never fire or touch the ball.
     */
    resume(t: number) {
        this.armed = false;
        this.blockedAt = -Infinity;
        this.minPull = this.pull;
        this.samples = [];
        this.lastT = t;
    }

    /** An unarmed push hit the stop recently: the player should pull back first. */
    blocked(now: number, ms = 1500): boolean {
        return !this.armed && now - this.blockedAt < ms;
    }

    /** Cue distance for rendering. */
    get distance() {
        return STROKE.REST_DISTANCE + this.pull / STROKE.PX_PER_UNIT;
    }

    /**
     * Feed one mouse movement (dy > 0: mouse pulled back). `t` is the event timestamp in ms
     * (event.timeStamp / performance.now()).
     */
    move(mouseDy: number, t: number): StrokeResult {
        const dy = mouseDy * this.sensitivity;
        if (!isFinite(dy) || dy === 0) return {fire: false, speed: 0};
        const span = this.eventSpan(t);
        this.lastT = t;
        this.samples.push({t, dy, span});
        const horizon = t - 250;
        while (this.samples.length && this.samples[0].t < horizon) this.samples.shift();

        const next = Math.min(STROKE.MAX_BACK_PX, this.pull + dy);
        if (dy > 0) {
            this.pull = next;
        } else {
            if (next <= -STROKE.FIRE_PX && this.armed) {
                this.pull = next;
                const speed = this.speed(t);
                this.reset(t);
                return {fire: true, speed};
            }
            // Without a back-stroke the cue stops short of the ball, with a visible gap; it never fires.
            const stop = -STROKE.FIRE_PX + STROKE.UNARMED_GAP_PX;
            if (!this.armed && next < stop) this.blockedAt = t;
            this.pull = this.armed ? next : Math.max(next, stop);
        }
        this.minPull = Math.min(this.minPull, this.pull);
        if (this.pull - this.minPull >= STROKE.MIN_BACK_PX) { this.armed = true; this.blockedAt = -Infinity; }
        return {fire: false, speed: 0};
    }

    /**
     * Forward cue speed (ball units) over the last WINDOW_MS, from event timestamps.
     *
     * The forward push starts at the last turnaround of the stroke: walking back in time from now,
     * the cue gets further back until we pass the peak of the back-stroke. A dip of less than
     * REVERSAL_PX on the way is hand jitter and doesn't end the push, so the measurement is the NET
     * displacement over the window (small backward wobbles subtract instead of restarting it).
     * Each event's movement happened between the previous event and itself, so it is spread over
     * that gap. The first event of the push has no reliable start time (it follows the turnaround or a
     * pause), so when the push started inside the window and has more events, it is left out.
     * The result is the same whether the browser delivers 5 or 200 events for the same stroke.
     * The event that reaches the ball (the last sample, at `now`) is always part of the measurement. When it is
     * also the FIRST event of the push (the cue reaches the ball in one coalesced event after a rest at the top),
     * its movement is spread over its gap (at most MAX_EVENT_SPAN_MS), so an extremely fast push after a short rest
     * can read below the cap, depending on where the frame boundary falls (round 8: 52.06; same in 907cf00).
     */
    speed(now: number): number {
        const n = this.samples.length;
        if (n === 0) return 0;
        const from = now - STROKE.WINDOW_MS;
        // pos = how far back the cue was before sample i, relative to now
        let pos = 0, peak = 0, runStart = n, i = n - 1, turnaround = false;
        for (; i >= 0; i--) {
            const smp = this.samples[i];
            if (smp.t <= from) break;                              // older movement is outside the window
            pos -= smp.dy;
            if (pos > peak) { peak = pos; runStart = i; }
            else if (pos < peak - STROKE.REVERSAL_PX) { turnaround = true; break; }   // passed the turnaround
        }
        // No turnaround inside the window: the push covers the whole window, so measure all of it
        // (net displacement; starting at the noisiest "most back" sample would bias the reading up).
        if (!turnaround) {
            runStart = Math.min(runStart, i + 1);
            // ...but not the tail of a back-stroke that happens to fall inside the window: the push starts at
            // the top of the back-stroke, wherever the cue was furthest back (see backStrokeTop).
            runStart = this.backStrokeTop(runStart);
            while (runStart < n - 1 && this.samples[runStart].dy > 0) runStart++;
        }
        if (runStart >= n || peak <= 0) return 0;
        let usable = this.samples.slice(runStart);
        // the push began at a turnaround (or after a reset/pause) rather than continuing from before the window
        const startedInWindow = runStart === 0 || this.samples[runStart - 1].dy > 0;
        if (usable.length > 1 && startedInWindow) usable = usable.slice(1);
        // A pause inside the push (typically a short stop at the top of the back-stroke, with sensor wobble
        // around it): the event that ends the pause moved at the end of a long gap, so spreading it over the
        // gap would under-read. It is recognised by a span much longer than the events after it; the push is
        // measured from the event after it. The last event is never dropped, so a single coalesced event after
        // a browser stall is still spread over its whole gap (can under-read, never inflates).
        for (let k = usable.length - 2; k >= 0; k--) {
            const next = usable.slice(k + 1, k + 9).map(x => x.span);
            if (next.length >= 2 && usable[k].span > 2 * median(next) + 1) { usable = usable.slice(k + 1); break; }
        }
        let dist = 0, start = now;
        for (const s of usable) {
            const s0 = s.t - s.span, s1 = s.t;
            const a = Math.max(s0, from);
            if (s1 <= a) continue;
            dist += -s.dy * ((s1 - a) / (s1 - s0));
            start = Math.min(start, a);
        }
        const dt = now - start;
        if (dt <= 0 || dist <= 0) return 0;
        const v = (dist / dt) * STROKE.GAIN;
        return Math.min(STROKE.MAX_SPEED, Math.max(STROKE.MIN_SPEED, v));
    }

    /**
     * Index of the first sample of the forward push, when the window [first, n) may still contain the end
     * of the back-stroke. The top of the back-stroke is where the cue was furthest back. A high-rate mouse
     * (500 Hz-1 kHz) reports small forward wobbles during a slow pull-back, so "the first forward sample"
     * is not the turnaround: the peak is found on the position smoothed over +-SMOOTH_MS (so wobble
     * doesn't pick a fake peak), then refined to the furthest-back raw sample next to it (exact when there
     * is no wobble). If the cue was furthest back at the start of the window, the whole window is the push.
     */
    private backStrokeTop(first: number): number {
        const n = this.samples.length;
        if (n - first < 3) return first;
        // P[k] = how far back the cue is after sample k (k = first-1 is the start of the window)
        const P: number[] = [], T: number[] = [];
        let acc = 0;
        P.push(0); T.push(first > 0 ? this.samples[first - 1].t : this.samples[first].t - this.samples[first].span);
        for (let k = first; k < n; k++) { acc += this.samples[k].dy; P.push(acc); T.push(this.samples[k].t); }
        const H = STROKE.SMOOTH_MS;
        let best = 0, bestS = -Infinity;
        let lo = 0, hi = 0, sum = 0;
        for (let k = 0; k < P.length - 1; k++) {           // the last sample is the contact, never the top
            while (hi < P.length && T[hi] <= T[k] + H) sum += P[hi++];
            while (T[lo] < T[k] - H) sum -= P[lo++];
            const sm = sum / (hi - lo);
            if (sm > bestS) { bestS = sm; best = k; }
        }
        // refine to the furthest-back raw position within +-H of the smoothed peak
        let top = best;
        for (let k = 0; k < P.length - 1; k++) if (Math.abs(T[k] - T[best]) <= H && P[k] > P[top]) top = k;
        if (P[top] <= 0) return first;                       // never further back than the window start
        return first + top;                                  // samples after the top are the push
    }

    /** Live forward speed for the power indicator. */
    liveSpeed(now: number): number {
        if (this.samples.length === 0 || now - this.samples[this.samples.length - 1].t > 120) return 0;
        return this.speed(now);
    }
}
